// Helper functions for URL parameter management

export interface URLFilterParams {
    origin?: string;
    destination?: string;
    stops?: string[];
    countries?: string[];
    services?: string[];
    radius?: number;
    lng?: string;
}

/**
 * Generates a shareable URL with all filters and route information
 */
export const generateShareableURL = (params: URLFilterParams): string => {
    const urlParams = new URLSearchParams();

    if (params.lng) {
        urlParams.set('lng', params.lng);
    }

    if (params.origin) {
        urlParams.set('origin', params.origin);
    }

    if (params.destination) {
        urlParams.set('destination', params.destination);
    }

    if (params.stops && params.stops.length > 0) {
        urlParams.set('stops', params.stops.join('|'));
    }

    if (params.countries && params.countries.length > 0) {
        urlParams.set('countries', params.countries.join(','));
    }

    if (params.services && params.services.length > 0) {
        urlParams.set('services', params.services.join(','));
    }

    if (params.radius) {
        urlParams.set('radius', params.radius.toString());
    }

    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?${urlParams.toString()}`;
};

/**
 * Parses URL parameters to restore filter state
 */
export const parseURLFilters = (): URLFilterParams => {
    const urlParams = new URLSearchParams(window.location.search);

    return {
        origin: urlParams.get('origin') || undefined,
        destination: urlParams.get('destination') || undefined,
        stops: urlParams.get('stops')?.split('|').filter(s => s) || [],
        countries: urlParams.get('countries')?.split(',').filter(c => c) || [],
        services: urlParams.get('services')?.split(',').filter(s => s) || [],
        radius: urlParams.get('radius') ? parseInt(urlParams.get('radius')!) : undefined,
        lng: urlParams.get('lng') || undefined,
    };
};

/**
 * Updates the browser URL without reloading the page
 */
export const updateURLWithFilters = (params: URLFilterParams): void => {
    const url = generateShareableURL(params);
    window.history.replaceState({}, '', url);
};

/**
 * Copies the shareable URL to clipboard
 */
export const copyShareableURLToClipboard = async (params: URLFilterParams): Promise<boolean> => {
    try {
        const url = generateShareableURL(params);
        await navigator.clipboard.writeText(url);
        return true;
    } catch (error) {
        console.error('Error copying to clipboard:', error);
        return false;
    }
};

